import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import IndexChipRows from './IndexChipRows';
import UpdatingBar from './UpdatingBar';
import { Skel } from './Skeleton';
import usePinnedBar from './usePinnedBar';
import { marketColor } from './marketColors';
import { PANEL_BG, panelClass, PinnedFade } from './pinnedPanel';
import { createPortal } from 'react-dom';
import { FILTER_ROW, SelectorToggle, PeriodControl, RangeInputs, IncludeOpenToggle, ThresholdSelect } from './FilterControls';
import { calendarYears, yearRange, yearOfRange } from '../utils/calendarYears';
import { apiService, loadUniverseShorts } from '../services/api';
import { GAIN_PRESETS, DEFAULT_MIN_GAIN, flattenTrades, buildBestTrades, overviewRows, marketsOf, yearSummary } from '../utils/bestTrades';
import { isCanceled } from '../utils/latestRequest';

/**
 * Strategy → Best Trades (Apal 2026-10-03): for the indexes I select, does
 * every quarter (or year) bring a great trade? Lists every period of the
 * history, newest first, with its top 3 trades at or above a chosen gain.
 *
 * Trades come from the Signal Analysis endpoint over the FULL history, so
 * Model / Costs / Execution, the L / L+S choice per index and the shorts
 * grants all apply exactly as they do there. All grouping math lives in
 * utils/bestTrades.js.
 */

const FULL_HISTORY_FROM = '1900-01-01';
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const ddmmyyyy = (s) => (s ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : '');
const pct = (g) => {
  const abs = Math.abs(g);
  const body = abs >= 1000 ? Math.round(abs).toLocaleString() : abs.toFixed(1);
  return `${g > 0 ? '+' : g < 0 ? '−' : ''}${body}%`;
};
const readJson = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key)); return v ?? fallback; } catch { return fallback; }
};


// Whole percent ("+158%"), thousands as "1.2k", so the gain column stays narrow.
const pct0 = (g) => {
  const a = Math.abs(g);
  return `${g > 0 ? '+' : g < 0 ? '−' : ''}${a >= 1000 ? `${(a / 1000).toFixed(1)}k` : `${Math.round(a)}%`}`;
};
const yearId = (y) => `bt-year-${y}`;

/**
 * One colour per market, everywhere on the page — split bar, quarter chips,
 * the edge of every trade line — so "which market won" reads at a glance
 * (Apal 2026-10-03: "this is kind important how can we highlight that
 * better?"). Hues avoid the page's own meanings: green = gain, orange = SHORT.
 */
// (MARKET_COLOR / marketColor live in ./marketColors, shared with Signal Analysis' Brief view)

/**
 * What is highlighted: any mix of markets and indexes (Apal 2026-10-04:
 * "multi select for markets and indexes allow them") — an array of picks
 * { kind: 'market' | 'index', key, label, market }. A trade is lit when it
 * matches ANY pick; a pick keeps its market's colour, and a quarter lit by
 * picks from more than one market gets a neutral border.
 */
const pickMatch = (f, t) => (f.kind === 'market' ? t.market === f.key : t.symbol === f.key);
const focusMatch = (focus, t) => focus.length > 0 && focus.some(f => pickMatch(f, t));
const focusMarket = (m) => ({ kind: 'market', key: m, label: m, market: m });
const NEUTRAL = '#e2e8f0';
/** One market's colour when every lit trade/pick shares it, else neutral. */
const litColor = (markets) => { const u = [...new Set(markets)]; return u.length === 1 ? marketColor(u[0]) : NEUTRAL; };
const focusName = (focus) => (focus.length <= 3 ? focus.map(f => f.label).join(' + ') : `${focus.length} selected`);
const isPicked = (focus, kind, key) => focus.some(f => f.kind === kind && f.key === key);

/** Indexes to highlight — a checkbox list grouped by market, each with its quarters won. */
const IndexPicker = ({ markets, indexes, complete, focus, onToggle }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const picked = focus.filter(f => f.kind === 'index');
  const text = picked.length === 0 ? 'All indexes' : picked.length <= 2 ? picked.map(f => f.label).join(', ') : `${picked.length} indexes`;
  return (
    <div ref={ref} className="relative inline-flex items-center gap-2 shrink-0">
      <span className="text-[11px] uppercase tracking-widest font-semibold text-dark-400">Index</span>
      <button type="button" data-testid="focus-index" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={picked.length ? { borderColor: litColor(picked.map(f => f.market)), color: litColor(picked.map(f => f.market)) } : undefined}
        className="h-8 inline-flex items-center gap-2 bg-dark-700 border border-dark-600 rounded-lg px-2.5 text-sm text-white font-medium max-w-[16rem]">
        <span className="truncate">{text}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true" data-testid="focus-index-list"
          className="absolute right-0 top-full mt-1.5 z-30 w-72 max-h-[60vh] overflow-y-auto rounded-xl border border-dark-600 bg-dark-800 shadow-2xl shadow-black/60 py-1.5">
          {markets.map(m => {
            const list = indexes.filter(i => i.market === m.market);
            if (!list.length) return null;
            return (
              <div key={m.market} className="py-1">
                <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: marketColor(m.market) }}>{m.market}</div>
                {list.map(i => {
                  const on = isPicked(focus, 'index', i.symbol);
                  return (
                    <label key={i.symbol} className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-dark-700/70">
                      <input type="checkbox" data-symbol={i.symbol} checked={on} className="accent-primary-500"
                        onChange={() => onToggle({ kind: 'index', key: i.symbol, label: i.label, market: i.market })} />
                      <span className={`flex-1 ${on ? 'text-white font-semibold' : 'text-dark-200'}`}>{i.label}</span>
                      <span className="text-xs tabular-nums text-dark-400">{i.periodsHit}/{complete} qtrs</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** A quarter's (or year's) markets, most trades first; the leader is filled — that market won it. */
const MarketTags = ({ hits, onFocus }) => (
  <span className="flex flex-wrap items-center gap-1" data-testid="market-tags">
    {marketsOf(hits).map((m, i) => {
      const c = marketColor(m.market);
      const lead = i === 0;
      return (
        <span key={m.market} data-lead={lead ? 'true' : undefined} role="button" tabIndex={0}
          onClick={() => onFocus && onFocus(focusMarket(m.market))}
          onKeyDown={(e) => { if (onFocus && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onFocus(focusMarket(m.market)); } }}
          title={`${m.count} trade${m.count === 1 ? '' : 's'} from ${m.market}${lead ? ' — most this period' : ''}`}
          style={lead ? { background: c, borderColor: c, color: '#0b1220' } : { background: `${c}14`, borderColor: `${c}66`, color: c }}
          className="inline-flex items-center gap-1 px-1.5 py-px rounded-md border text-[10px] font-bold uppercase tracking-wide cursor-pointer hover:brightness-125">
          {m.market}<span className="tabular-nums" style={{ opacity: lead ? 0.75 : 0.8 }}>{m.count}</span>
        </span>
      );
    })}
  </span>
);

/**
 * Share of every qualifying trade by market — and the FOCUS control (Apal
 * 2026-10-03: "check if any of the indian market … trade came in a quarter
 * or not … by highlighting"; "the selection part should … stick up as users
 * scroll"). Click a market to light it up across every year; click again to
 * clear. Pins to the top of the window while the years scroll beneath it.
 */
const MarketFocusBar = ({ markets, indexes, minGain, unit, complete, focus, onFocus, onClear, endRef }) => {
  const slotRef = useRef(null);
  const barRef = useRef(null);
  const { pin, barHeight } = usePinnedBar(slotRef, barRef, endRef, [markets.length]);
  return (
    <div ref={slotRef} className="mb-8" style={pin ? { minHeight: barHeight } : undefined}>
      {/* Its own surface (Apal 2026-10-04: "show that selection panel as clear
          as possible not getting confused with background"): lighter than the
          #1e293b year cards it floats over, with a blue edge. Pinned, it gains
          a blue bottom rule, a deep shadow and a fade beneath, so the years
          visibly slide UNDER it. */}
      <div ref={barRef} data-testid="market-split" data-pinned={pin ? 'true' : undefined}
        style={pin ? { position: 'fixed', top: 0, left: pin.left, width: pin.width, background: PANEL_BG } : { background: PANEL_BG }}
        className={`z-20 px-4 pt-3 pb-3 ${panelClass(!!pin)}`}>
        <div className="flex items-center justify-between gap-4 mb-2">
          <span className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold">
            {minGain}%+ trades by market · complete {unit}s
          </span>
          <IndexPicker markets={markets} indexes={indexes} complete={complete} focus={focus} onToggle={onFocus} />
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-dark-700">
          {markets.map(m => (
            <div key={m.market} title={`${m.market} ${m.pct}% (${m.count})`}
              style={{ width: `${m.pct}%`, background: marketColor(m.market), opacity: focus.length && !focus.some(f => f.market === m.market) ? 0.25 : 1 }} />
          ))}
        </div>
        {/* A filter row in the page's own vocabulary: label, "All" selected by default */}
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-dark-400 mr-1">Highlight</span>
          <button type="button" data-testid="clear-focus" aria-pressed={!focus.length} onClick={onClear}
            className={`px-3 py-1 rounded-lg border text-sm font-semibold transition ${
              !focus.length ? 'bg-primary-600 border-primary-600 text-white' : 'border-dark-600 text-dark-300 hover:text-white hover:bg-dark-700/60'}`}>
            All
          </button>
          {markets.map(m => {
            const c = marketColor(m.market);
            const on = isPicked(focus, 'market', m.market);
            return (
              <button key={m.market} type="button" data-market={m.market} aria-pressed={on}
                onClick={() => onFocus(focusMarket(m.market))}
                title={`${m.market}: ${m.count} trades ${minGain}%+, in ${m.periodsHit} of ${complete} ${unit}s`}
                style={on ? { background: c, borderColor: c, color: '#0b1220' } : { borderColor: `${c}55` }}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-sm transition ${
                  on ? 'font-semibold' : focus.length && !focus.some(f => f.market === m.market) ? 'opacity-50 hover:opacity-100' : 'hover:bg-dark-700/60'}`}>
                {!on && <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />}
                <span className={on ? '' : 'font-semibold text-dark-100'}>{m.market}</span>
                <span className="tabular-nums" style={on ? undefined : { color: '#cbd5e1' }}>{m.pct}%</span>
                <span className={`tabular-nums text-xs ${on ? '' : 'text-dark-400'}`}>{m.periodsHit}/{complete} {unit === 'quarter' ? 'qtrs' : 'yrs'}</span>
              </button>
            );
          })}
        </div>
        <PinnedFade show={!!pin} />
      </div>
    </div>
  );
};

/** The year's title with its highlights: quarters won, its 10%+ trades and their average, its best trade. */
const YearHeader = ({ year, cells, minGain, focus }) => {
  const y = yearSummary(cells);
  const done = cells.filter(c => c && c.status === 'complete');
  const focusWon = focus.length ? done.filter(c => c.hits.some(t => focusMatch(focus, t))).length : 0;
  const fc = litColor(focus.map(f => f.market));
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mb-2.5" data-testid="year-summary">
      <h3 className="text-lg font-bold text-white tabular-nums">{year}</h3>
      {y.qComplete > 0 && (
        <span className="text-sm text-dark-400" data-stat="quarters">
          <b className="text-white tabular-nums">{y.qHit}/{y.qComplete}</b> quarters with a {minGain}%+ trade
        </span>
      )}
      {y.count > 0 && (
        <span className="text-sm text-dark-400" data-stat="trades">
          <b className="text-white tabular-nums">{y.count}</b> trade{y.count === 1 ? '' : 's'} {minGain}%+ · avg{' '}
          <b className="text-emerald-400 tabular-nums">{pct(y.avgGain)}</b>
        </span>
      )}
      {focus.length > 0 && done.length > 0 && (
        <span data-stat="focus" className="text-sm font-semibold px-2 py-0.5 rounded-md border" title={focus.map(f => f.label).join(' + ')}
          style={{ color: fc, borderColor: `${fc}66`, background: `${fc}14` }}>
          {focusName(focus)} {focusWon}/{done.length} quarters
        </span>
      )}
      {y.best && (
        <span className="text-sm text-dark-400 inline-flex items-baseline gap-1.5" data-stat="best"
          title={`${y.best.symbol_label} (${y.best.market}) · ${y.best.direction === 'SHORT' ? 'Short' : 'Long'} · ${ddmmyyyy(y.best.entry_date)} → ${ddmmyyyy(y.best.exit_date)}`}>
          best
          <span className="w-2 h-2 rounded-full self-center" style={{ background: marketColor(y.best.market) }} />
          <b className="text-dark-100">{y.best.symbol_label}</b>
          <b className="text-emerald-400 tabular-nums">{pct(y.best.gain)}</b>
        </span>
      )}
    </div>
  );
};

// Days held, in a 32px column: a 1,000+ day hold reads in years ("1148d" → "3.1y"),
// the exact days stay in the tooltip — widening the column would clip names.
const held = (d) => (d >= 1000 ? `${(d / 365).toFixed(1)}y` : `${d}d`);

/**
 * The quarter we're in now (Apal 2026-10-04: "on our app we have that live
 * heartbeat … Running is confusing … just show you are in this quarter, which
 * is active right now"). The admin's own live pulse — the same ping the
 * dashboard's "Open" badge and the app's open-market rings use.
 */
/**
 * A quarter with no trade at the bar is a result too (Apal 2026-10-15: "an
 * empty quarter should show clearly being empty as that's something we want
 * to show as well"). A finished quarter says so plainly inside a dashed frame;
 * the quarter we're in only says "none yet"; a quarter the range cuts says
 * it covers part of it. A lone faint dash read as a rendering gap.
 */
export const EmptyQuarter = ({ status, minGain }) => {
  const missed = status === 'complete';          // a finished quarter with nothing: a result
  const text = status === 'in_progress' ? 'None yet'
    : status === 'partial' ? `No ${minGain}%+ trade in this part`
    : `No ${minGain}%+ trade`;
  return (
    <div data-testid="empty-quarter" data-status={status}
      className={`mx-0.5 flex flex-1 min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-2 text-center ${
        missed ? 'border-dark-500/80' : 'border-dark-600'}`}
      // the familiar "empty slot" hatch — only on a finished quarter, so a
      // quarter still running never looks like a miss
      style={missed ? { backgroundImage: 'repeating-linear-gradient(135deg, rgba(148,163,184,0.07) 0 6px, transparent 6px 12px)' } : undefined}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"
        className={missed ? 'text-dark-300' : 'text-dark-500'}>
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6.5 17.5 17.5 6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      <span className={`text-xs font-semibold ${missed ? 'text-dark-200' : 'text-dark-400'}`}>{text}</span>
    </div>
  );
};

const CurrentQuarterTag = () => (
  <span data-testid="current-quarter" title="The quarter we're in now — its trades so far. Not counted in the stats until it ends."
    className="shrink-0 inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-green-500/20 text-green-400 border border-green-500/30">
    <span className="relative flex h-2 w-2">
      <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
    </span>
    Current quarter
  </span>
);

/** One trade per line: direction, index, profit, days. Dates live in the tooltip. */
const TradeLine = ({ t, dim = false, onPick }) => {
  const short = t.direction === 'SHORT';
  const tip = `${t.symbol_label} (${t.market}) · ${short ? 'Short' : 'Long'} · ${ddmmyyyy(t.entry_date)} → ${t.is_open ? 'still open' : ddmmyyyy(t.exit_date)}${
    t.days_held != null ? ` · ${t.days_held} days` : ''}`;
  return (
    <div title={tip} data-testid="trade-line" data-market={t.market} data-dim={dim ? 'true' : undefined}
      onClick={onPick ? () => onPick(t) : undefined}
      style={{ boxShadow: `inset 2px 0 0 ${marketColor(t.market)}` }}
      className={`flex items-center gap-1 h-[26px] pl-1.5 pr-1 rounded-r transition-opacity ${onPick ? 'cursor-pointer hover:bg-white/[0.04]' : ''} ${t.is_open ? 'bg-yellow-900/20' : ''} ${dim ? 'opacity-25' : ''}`}>
      <span className={`w-[2.25rem] shrink-0 text-center text-[9px] font-bold tracking-wide py-px rounded ${short ? 'bg-orange-500/20 text-orange-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
        {short ? 'SHORT' : 'LONG'}
      </span>
      <span className="flex-1 min-w-0 truncate text-[12.5px] text-dark-100">{t.symbol_label}</span>
      <span className={`shrink-0 text-[13px] font-semibold tabular-nums ${t.is_open ? 'text-yellow-300' : 'text-emerald-400'}`}>{pctLine(t.gain)}</span>
      <span className={`w-8 shrink-0 text-right text-[11px] tabular-nums ${t.is_open ? 'text-yellow-400/80' : 'text-dark-500'}`}>
        {t.is_open ? 'open' : t.days_held != null ? held(t.days_held) : ''}
      </span>
    </div>
  );
};

// Trade lines: one decimal, whole numbers from 100% up so the column stays narrow.
const pctLine = (g) => (Math.abs(g) >= 99.95 ? pct0(g) : pct(g));


// `today` is injectable so tests and the preview can pin the running quarter.
const BestTrades = ({ strategy, executionMode, netCosts, costConfig, reportSource, reloadKey, today: todayProp, filterSlot = null }) => {
  const today = todayProp || localToday();
  // ── Index selection (same chips + L / L+S toggle as Signal Analysis) ──
  const [regionSymbols, setRegionSymbols] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [selected, setSelected] = useState(() => readJson('strategy.bestTradesSelected', null)); // null = first visit
  const [perSymStrategy, setPerSymStrategy] = useState(() => readJson('strategy.bestTradesModes', {}));
  const [shortsSymbols, setShortsSymbols] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(false);

  // ── Page filters ──
  // Quarters only: each year's header carries its own highlights (a separate
  // Year view repeated the same trades without the quarters).
  const grain = 'quarter';
  const [minGain, setMinGain] = useState(() => {
    const v = Number(localStorage.getItem('strategy.bestTradesMinGain'));
    return GAIN_PRESETS.includes(v) ? v : DEFAULT_MIN_GAIN;
  });
  const [from, setFrom] = useState(() => localStorage.getItem('strategy.bestTradesFrom') || '');
  const [to, setTo] = useState(() => localStorage.getItem('strategy.bestTradesTo') || '');
  // Which Period choice is lit — read from the saved dates on load (none →
  // All history, exactly 1 Jan – 31 Dec → that Year, anything else → Custom)
  const [periodMode, setPeriodMode] = useState(() => {
    const f = localStorage.getItem('strategy.bestTradesFrom') || '';
    const t = localStorage.getItem('strategy.bestTradesTo') || '';
    return !f && !t ? 'all' : yearOfRange(f, t) ? 'year' : 'custom';
  });
  const [includeOpen, setIncludeOpen] = useState(true);
  const [focus, setFocus] = useState([]);   // picks lit up across the years (markets and/or indexes)
  const toggleFocus = (f) => setFocus(prev => (isPicked(prev, f.kind, f.key)
    ? prev.filter(p => !(p.kind === f.kind && p.key === f.key)) : [...prev, f]));
  const pickIndex = (t) => toggleFocus({ kind: 'index', key: t.symbol, label: t.symbol_label, market: t.market });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { if (selected) localStorage.setItem('strategy.bestTradesSelected', JSON.stringify(selected)); }, [selected]);
  useEffect(() => { localStorage.setItem('strategy.bestTradesModes', JSON.stringify(perSymStrategy)); }, [perSymStrategy]);
  useEffect(() => { localStorage.setItem('strategy.bestTradesMinGain', String(minGain)); }, [minGain]);
  useEffect(() => { localStorage.setItem('strategy.bestTradesFrom', from); }, [from]);
  useEffect(() => { localStorage.setItem('strategy.bestTradesTo', to); }, [to]);

  useEffect(() => {
    loadUniverseShorts().then(s => setShortsSymbols([...s])).catch(() => setShortsSymbols([]));
    apiService.getPeriodPerformance({ market: 'all', strategy: '1x' }).then(res => {
      setRegionSymbols((res.data?.regions || []).map(r => ({
        name: r.region, code: r.code,
        symbols: r.indexes.map(i => ({ name: i.symbol, label: i.label })),
      })));
    }).catch(() => {}).finally(() => setRegionsLoading(false));
  }, []);

  // First visit: every index the user can see — the question is "across my book".
  useEffect(() => {
    if (selected === null && regionSymbols.length) setSelected(regionSymbols.flatMap(r => r.symbols.map(s => s.name)));
  }, [selected, regionSymbols]);

  const sel = useMemo(() => selected || [], [selected]);

  const toggle = (sym) => setSelected(prev => (prev || []).includes(sym) ? prev.filter(s => s !== sym) : [...(prev || []), sym]);
  const toggleRegion = (code) => {
    const region = regionSymbols.find(r => r.code === code);
    if (!region) return;
    const names = region.symbols.map(s => s.name);
    const all = names.every(n => sel.includes(n));
    setSelected(prev => all ? (prev || []).filter(s => !names.includes(s)) : [...new Set([...(prev || []), ...names])]);
  };
  // No L+S on the chip → long-only, even if an old 'with_shorts' is still in
  // storage from before the index lost its toggle (Apal 2026-10-12: Smallcap).
  const modeOf = (sym) => ((shortsSymbols || []).includes(sym) ? (perSymStrategy[sym] || 'with_shorts') : '1x');

  const costParams = useMemo(() => {
    if (!netCosts || !['1x', 'with_shorts', 'shorts_only'].includes(strategy)) return {};
    const params = { net_costs: 'true' };
    Object.entries(costConfig || {}).forEach(([sym, cfg]) => {
      if (cfg && typeof cfg === 'object') {
        params[`cost_${sym}`] = cfg.impact ?? 0;
        params[`carry_${sym}`] = cfg.carry_pct ?? 0;
        params[`intrate_${sym}`] = cfg.interest_rate ?? 0;
        params[`margin_${sym}`] = cfg.margin_pct ?? 20;
      }
    });
    return params;
  }, [netCosts, costConfig, strategy]);

  // One fetch for the whole history; grouping, threshold and dates are local.
  useEffect(() => {
    if (shortsSymbols === null || selected === null) return;
    if (!sel.length) { setData(null); setLoading(false); return; }
    let cancelled = false;
    const abort = new AbortController();     // a newer filter click cancels this one
    setLoading(true);
    setError(false);
    const started = Date.now();
    apiService.getSignalAnalysis({
      symbols: sel.join(','),
      strategy,
      sym_strategies: sel.map(s => `${s}:${modeOf(s)}`).join(','),
      period: 'custom',
      from_date: FULL_HISTORY_FROM,
      to_date: today,
      ...(executionMode !== 'same_day_close' ? { execution: executionMode } : {}),
      ...(reportSource === 'latest' ? { source: 'latest' } : {}),
      ...costParams,
    }, { signal: abort.signal }).then(async res => {
      // shown at least 400ms (as on Signal Analysis) so a quick answer reads as an update, not a flicker
      const left = 400 - (Date.now() - started);
      if (left > 0) await new Promise(r => setTimeout(r, left));
      if (!cancelled) setData(res.data);
    })
      .catch((e) => { if (!cancelled && !isCanceled(e)) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; abort.abort(); };
    // modeOf reads perSymStrategy + shortsSymbols, both listed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, selected, strategy, perSymStrategy, shortsSymbols, executionMode, costParams, reportSource, reloadKey, today]);

  const rangeBad = from && to && from > to;
  const years = useMemo(() => calendarYears(Number(today.slice(0, 4))), [today]); // the running year has quarters too
  const pickedYear = yearOfRange(from, to);
  const pickAll = () => { setPeriodMode('all'); setFrom(''); setTo(''); };
  const pickYear = (y) => { const r = yearRange(y); setPeriodMode('year'); setFrom(r.from); setTo(r.to); };
  const { periods, summary } = useMemo(
    () => buildBestTrades(flattenTrades(data?.regions), { grain, minGain, from: rangeBad ? '' : from, to: rangeBad ? '' : to, today, includeOpen }),
    [data, grain, minGain, from, to, today, includeOpen, rangeBad]
  );
  const grid = useMemo(() => overviewRows(periods, grain), [periods, grain]);
  const completeSpan = useMemo(() => {
    const c = periods.filter(p => p.status === 'complete');
    return c.length ? (c.length === 1 ? c[0].label : `${c[c.length - 1].label} – ${c[0].label}`) : '';
  }, [periods]);
  const unit = grain === 'quarter' ? 'quarter' : 'year';
  const isNet = !!netCosts; // Net view: every % is after costs

  // Four quarters across when the page is wide enough, else two rows of two.
  const listRef = useRef(null);
  const [listWidth, setListWidth] = useState(1100);
  const hasPeriods = periods.length > 0;
  // Layout effect + an immediate measure: the right column count from the
  // first paint, instead of four columns until ResizeObserver's first callback
  // (which a backgrounded tab can hold back).
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    setListWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setListWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasPeriods]);

  // Newest year first, Q1 → Q4 inside each.
  const yearBlocks = grid;
  const wideLayout = listWidth >= 1000;

  return (
    <div data-testid="best-trades">
      {/* ── Filters ── */}
      <div data-testid="best-trades-filters" className="space-y-4 pb-6 mb-6 border-b border-dark-700">
      {/* Index selection — collapsed to its count; the chips open on click */}
      <div>
        <SelectorToggle open={selectorOpen} onToggle={() => setSelectorOpen(!selectorOpen)} count={selected === null ? null : sel.length} />
        {selectorOpen && (
          <div className="bg-dark-800/50 rounded-lg border border-dark-700 p-4">
            <IndexChipRows
              regions={regionSymbols}
              loading={regionsLoading}
              selected={sel}
              onToggle={toggle}
              onRegionToggle={toggleRegion}
              canShort={n => (shortsSymbols || []).includes(n)}
              modeFor={n => (modeOf(n) === 'with_shorts' ? 'ls' : 'l')}
              onModeToggle={n => setPerSymStrategy(prev => ({ ...prev, [n]: modeOf(n) === 'with_shorts' ? '1x' : 'with_shorts' }))}
            />
            <div className="flex items-center gap-3 mt-3 pt-2 border-t border-dark-700">
              {sel.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-dark-400 hover:text-red-400">Clear all</button>}
              {regionSymbols.length > 0 && (
                <button onClick={() => setSelected(regionSymbols.flatMap(r => r.symbols.map(s => s.name)))} className="text-xs text-dark-400 hover:text-teal-400">Select all</button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Page filters — Trades of at least · Period · Open trades join Model ·
          Costs · Execution in the ONE top bar, exactly like Compare and
          Signal Analysis. One label under Gross and Net alike: the Costs
          control already says which (Apal 2026-10-15: "shouldn't say net
          here"); the note below still explains why trades drop out under Net. */}
      {(() => {
        const filters = (
          <>
            <ThresholdSelect id="best-trades-min-gain" label="Trades of at least" testid="best-trades-min-gain"
              selectTestid="best-trades-min-gain-select" value={String(minGain)} onChange={v => setMinGain(Number(v))}
              options={GAIN_PRESETS.map(g => ({ value: String(g), label: `${g}%` }))} />
            <PeriodControl id="best-trades-period-label" testid="best-trades-period" mode={periodMode}
              presets={[{ key: 'all', label: 'All history' }]} onPreset={pickAll}
              years={years} pickedYear={pickedYear} onYear={pickYear}
              yearTestid="best-trades-year" yearLabelTestid="best-trades-year-label"
              onCustom={() => setPeriodMode('custom')}
              custom={<RangeInputs kind="date" from={from} to={to} onFrom={setFrom} onTo={setTo} />} />
            <IncludeOpenToggle checked={includeOpen} onChange={setIncludeOpen}
              title="On: an open trade counts in the current period at its latest price. Off: only closed trades." />
          </>
        );
        return filterSlot ? createPortal(filters, filterSlot) : <div className={FILTER_ROW}>{filters}</div>;
      })()}
      {rangeBad && <div className="text-xs text-red-400">The From date is after the To date — showing all history.</div>}
      </div>

      {sel.length === 0 && selected !== null && (
        <div className="text-dark-400 text-sm py-10 text-center border border-dashed border-dark-700 rounded-lg">Select indexes to see their best trades.</div>
      )}
      {error && <div className="text-red-400 text-sm py-6 text-center">Could not load trades. Try again in a moment.</div>}
      {/* Loading — always the same bar; first load also shows the page's shape,
          from the first moment (the index list and the shorts verdict come first) */}
      {(() => {
        const waiting = !data && !error && (sel.length > 0 || (selected === null && regionsLoading));
        return (
          <>
            {(loading || waiting) && (sel.length > 0 || selected === null) && <UpdatingBar label={data ? 'Updating...' : 'Loading trades...'} className="mb-6" />}
            {waiting && (
              <div data-testid="best-trades-skeleton" aria-hidden="true">
                {/* the page's own shape (Apal 2026-11-03: "exactly what's gonna load"):
                    the four figures by name, the market panel, then the newest
                    years, each with its quarters side by side */}
                <div className="mb-6">
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-px bg-dark-700 rounded-xl overflow-hidden border border-dark-700">
                    {[`${unit}s with a ${minGain}%+ trade`, 'Longest stretch without one', `Avg ${minGain}%+ trades per ${unit}`, isNet ? 'Avg net gain · typical hold' : 'Avg gain · typical hold'].map(label => (
                      <div key={label} className="bg-dark-800 px-6 py-4">
                        <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-2">{label}</div>
                        <div className="h-9 flex items-center"><Skel className="h-7 w-32" /></div>
                      </div>
                    ))}
                  </div>
                  <div className="h-4 mt-2.5 flex items-center"><Skel className="h-2.5 w-96" /></div>
                </div>
                <div className="px-4 pt-3 pb-3 rounded-xl border border-primary-500/30 mb-4" style={{ background: PANEL_BG }}>
                  <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-2">{minGain}%+ trades by market · complete {unit}s</div>
                  <Skel className="h-3 w-full block" rounded="rounded-full" />
                  <div className="flex items-center gap-1.5 mt-2.5">{[0, 1, 2, 3, 4, 5].map(i => <Skel key={i} className="h-7 w-28" />)}</div>
                </div>
                <div className="space-y-3">
                  {[0, 1, 2].map(k => (
                    <section key={k} className="rounded-xl border border-dark-700 bg-dark-800/40 px-4 pt-3 pb-4">
                      <div className="flex items-baseline gap-6 mb-2.5">
                        <h3 className="text-lg font-bold text-white tabular-nums">{new Date().getFullYear() - k}</h3>
                        <Skel className="h-3 w-48" /><Skel className="h-3 w-40" />
                      </div>
                      <div className={`grid gap-1.5 ${wideLayout ? 'grid-cols-4' : 'grid-cols-2'}`}>
                        {[1, 2, 3, 4].map(q => (
                          <div key={q} className="rounded-lg p-1.5 bg-dark-900/50">
                            <div className="px-1 text-xs font-semibold text-dark-300">Q{q}</div>
                            <div className="min-h-[22px] px-1 mt-1 mb-1.5 flex gap-1"><Skel className="h-[18px] w-12" /><Skel className="h-[18px] w-14" /></div>
                            <div className="space-y-2 px-1 pb-1">
                              {Array.from({ length: 5 - ((q + k) % 3) }, (_, i) => <Skel key={i} className="h-3.5 w-full block" />)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {data && sel.length > 0 && (
        <div className={`transition-opacity duration-300 ${loading ? 'opacity-40 pointer-events-none' : ''}`}>
          {/* ── Stats ── complete periods only */}
          <div className="mb-6">
            <div data-testid="best-trades-headline"
              className="grid grid-cols-2 xl:grid-cols-4 gap-px bg-dark-700 rounded-xl overflow-hidden border border-dark-700">
              <div className="bg-dark-800 px-6 py-4">
                <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-2">{unit}s with a {minGain}%+ trade</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-emerald-400 tabular-nums">{summary.hit}</span>
                  <span className="text-base font-semibold text-dark-400 tabular-nums whitespace-nowrap">of {summary.complete} {unit}s</span>
                  {summary.pct != null && <span className="text-base font-semibold text-emerald-300/80 tabular-nums whitespace-nowrap">· {summary.pct}%</span>}
                </div>
              </div>
              <div className="bg-dark-800 px-6 py-4">
                <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-2">Longest stretch without one</div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`text-3xl font-bold tabular-nums ${summary.longestGap ? 'text-amber-300' : 'text-emerald-400'}`}>
                    {summary.longestGap ? summary.longestGap.length : 'None'}
                  </span>
                  {summary.longestGap && <span className="text-lg font-semibold text-dark-400">{unit}{summary.longestGap.length === 1 ? '' : 's'}</span>}
                  {summary.longestGap && (
                    <span className="text-sm text-dark-400">
                      {summary.longestGap.length === 1 ? summary.longestGap.from : `${summary.longestGap.from} – ${summary.longestGap.to}`}
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-dark-800 px-6 py-4">
                <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-2">Avg {minGain}%+ trades per {unit}</div>
                <span className="text-3xl font-bold text-white tabular-nums">{summary.avgPerPeriod != null ? summary.avgPerPeriod.toFixed(1) : '—'}</span>
              </div>
              <div className="bg-dark-800 px-6 py-4">
                <div className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-2">{isNet ? 'Avg net gain · typical hold' : 'Avg gain · typical hold'}</div>
                <div className="flex flex-wrap items-baseline gap-x-2.5">
                  <span className="text-3xl font-bold text-emerald-400 tabular-nums">{summary.avgGain != null ? pct(summary.avgGain) : '—'}</span>
                  {summary.medianDays != null && (
                    <span className="text-base font-semibold text-dark-200 tabular-nums" data-stat="typical-hold"
                      title="Typical hold: the median days held — half of these trades took less, half took more">
                      {summary.medianDays} day{summary.medianDays === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-dark-500 mt-2.5">
              Each trade shows in the {unit} it closed, at its full gain; days show how long it was held.{completeSpan && ` Complete ${unit}s, ${completeSpan}.`}
            </p>
            {isNet && (
              <p data-testid="best-trades-net-note" className="text-xs text-amber-300/80 mt-1">
                Net: only trades that made {minGain}%+ after costs are shown. Smaller winners drop below the bar, so the average of those left can be higher than in Gross.
              </p>
            )}
          </div>

          {summary.markets.length > 0 && (
            <MarketFocusBar markets={summary.markets} indexes={summary.indexes} minGain={minGain} unit={unit} complete={summary.complete}
              focus={focus} onFocus={toggleFocus} onClear={() => setFocus([])} endRef={listRef} />
          )}

          {periods.length === 0 ? (
            <div className="text-dark-400 text-sm py-10 text-center">No trades closed in this range.</div>
          ) : (
            <>
              {/* Every year, newest first: one framed block, quarters side by side */}
              <div ref={listRef} className="space-y-3" data-testid="best-trades-years">
                {yearBlocks.map(({ year, cells }) => (
                  <section key={year} id={yearId(year)} data-year={year}
                    className="rounded-xl border border-dark-700 bg-dark-800/40 px-4 pt-3 pb-4 scroll-mt-4">
                    <YearHeader year={year} cells={cells} minGain={minGain} focus={focus} />
                    <div className={`grid gap-1.5 ${wideLayout ? 'grid-cols-4' : 'grid-cols-2'}`}>
                      {cells.map((p, i) => (
                          <div key={i} data-period={p?.key}
                            data-focus={p && focus.length ? (p.hits.some(t => focusMatch(focus, t)) ? 'hit' : 'miss') : undefined}
                            style={p && focus.length && p.hits.some(t => focusMatch(focus, t))
                              ? { boxShadow: `inset 0 0 0 1.5px ${litColor(p.hits.filter(t => focusMatch(focus, t)).map(t => t.market))}` } : undefined}
                            data-current={p?.status === 'in_progress' ? 'true' : undefined}
                            className={`rounded-lg p-1.5 min-w-0 transition-opacity ${p ? 'bg-dark-900/50' : ''} ${p && !p.hits.length ? 'flex flex-col' : ''} ${
                              // the quarter we're in: a green outline (no layout change), the live pulse in its header
                              p?.status === 'in_progress' ? 'outline outline-2 -outline-offset-2 outline-green-400/60' : ''} ${
                              p && focus.length && !p.hits.some(t => focusMatch(focus, t)) ? 'opacity-40' : ''}`}>
                            {p && (
                              <>
                                <div className="flex items-center justify-between gap-2 px-1">
                                  <span className="text-xs font-semibold text-dark-300">Q{p.q}</span>
                                  {p.status !== 'complete' && (
                                    p.status === 'in_progress'
                                      ? <CurrentQuarterTag />
                                      : <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-dark-400">Partial</span>
                                  )}
                                </div>
                                {/* its own line in every quarter, so trade lines start level across the year */}
                                <div className="min-h-[22px] px-1 mt-1 mb-1.5">{p.hits.length > 0 && <MarketTags hits={p.hits} onFocus={toggleFocus} />}</div>
                                {p.hits.length
                                  ? p.hits.map(t => <TradeLine key={`${t.symbol}-${t.entry_date}`} t={t} dim={focus.length > 0 && !focusMatch(focus, t)} onPick={pickIndex} />)
                                  : <EmptyQuarter status={p.status} minGain={minGain} />}
                              </>
                            )}
                          </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BestTrades;
